// BOORO: one file of the knowledge or the assets: a look at it where the
// browser can show it safely, what it is for, and the way to download, rename,
// replace with a new version or remove it.

import { Trans, useLingui } from "@lingui/react/macro";
import type { KnowledgeFileSummary, KnowledgeFolder, OrgSnapshot } from "@rakazo/contracts";
import { Button, Input } from "@rakazo/ui-web";
import { ArrowLeft, Building, Download, Globe, Trash2, Undo2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { MoveMenu } from "./knowledge-tree";
import { AccessRow, base64Of } from "./knowledge-view";

/** Text a browser shows as it is; anything else is only offered as a download. */
const TEXT_TYPES = ["text/plain", "text/markdown", "text/csv", "application/json"];

/** "1.2 MB" for a size in bytes. */
export function fileSize(bytes: number, locale?: string): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || value >= 10 ? 0 : 1;
  return `${value.toLocaleString(locale, { maximumFractionDigits: digits })} ${units[unit]}`;
}

export function bytesOf(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

export function KnowledgeFileView({
  fileId,
  snapshot,
  folders,
  onChanged,
  onRemoved,
  onBack,
}: {
  fileId: string;
  snapshot: OrgSnapshot | null;
  folders: readonly KnowledgeFolder[];
  onChanged: () => Promise<void>;
  onRemoved: () => void;
  /** Goes back to the folder the file lies in, where a folder shows its files. */
  onBack?: () => void;
}) {
  const { t, i18n } = useLingui();
  const [file, setFile] = useState<KnowledgeFileSummary | null>(null);
  const [bytes, setBytes] = useState<Uint8Array<ArrayBuffer> | null>(null);
  const [versions, setVersions] = useState(0);
  const [name, setName] = useState("");
  const [summary, setSummary] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reloads, setReloads] = useState(0);
  const versionInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let active = true;
    // A reload after a new version keeps the file on screen until the new bytes are there.
    if (reloads === 0) {
      setFile(null);
      setBytes(null);
    }
    void rpc.booroKnowledge
      .file({ fileId })
      .then((found) => {
        if (!active) return;
        setFile(found?.file ?? null);
        setName(found?.file.name ?? "");
        setSummary(found?.file.summary ?? "");
        setVersions(found?.versions ?? 0);
        setBytes(found ? bytesOf(found.contentBase64) : null);
        setError("");
      })
      .catch((cause) => {
        if (active) setError(errorText(cause));
      });
    return () => {
      active = false;
    };
  }, [fileId, reloads]);

  // The bytes as a blob URL, only for kinds the page shows inline.
  const preview = useMemo(() => {
    if (!file || !bytes) return null;
    const type = file.mimeType;
    // An <img> never runs what an SVG holds, so logos in SVG show too.
    const showable = type.startsWith("image/") || type === "application/pdf";
    if (!showable) return null;
    return URL.createObjectURL(new Blob([bytes], { type }));
  }, [file, bytes]);
  useEffect(() => () => (preview ? URL.revokeObjectURL(preview) : undefined), [preview]);

  const text = useMemo(() => {
    if (!file || !bytes || !TEXT_TYPES.includes(file.mimeType)) return null;
    return new TextDecoder().decode(bytes.slice(0, 200_000));
  }, [file, bytes]);

  function download() {
    if (!file || !bytes) return;
    // Always a download, never a page of this app: the bytes come from people and agents.
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function rename() {
    if (!file || !name.trim() || name.trim() === file.name) {
      setName(file?.name ?? "");
      return;
    }
    setBusy(true);
    try {
      const result = await rpc.booroKnowledge.renameFile({ fileId, name: name.trim() });
      setFile({ ...file, name: result.name });
      setName(result.name);
      setError("");
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
      setName(file.name);
    } finally {
      setBusy(false);
    }
  }

  async function describe() {
    if (!file || summary.trim() === file.summary) return;
    try {
      const result = await rpc.booroKnowledge.describeFile({ fileId, summary });
      setFile({ ...file, summary: result.summary });
      setSummary(result.summary);
      setError("");
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    }
  }

  /** Runs a change of the bytes, then shows the file as it is now. */
  async function changeBytes(run: () => Promise<unknown>) {
    setBusy(true);
    try {
      await run();
      setError("");
      setReloads((count) => count + 1);
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function move(folder: string) {
    if (!file) return;
    try {
      await rpc.booroKnowledge.move({ kind: "file", id: file.id, folder });
      setFile({ ...file, folder });
      setError("");
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await rpc.booroKnowledge.removeFile({ fileId });
      onRemoved();
    } catch (cause) {
      setError(errorText(cause));
      setBusy(false);
    }
  }

  if (!file) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  const placeName =
    file.spaceId === null
      ? t`Whole account`
      : (snapshot?.offices.find((office) => office.id === file.spaceId)?.name ?? "");
  const writer = file.createdByBotId
    ? (snapshot?.agents.find((agent) => agent.id === file.createdByBotId)?.name ?? t`an agent`)
    : null;

  return (
    <>
      <input
        ref={versionInput}
        type="file"
        hidden
        data-testid="knowledge-file-version-input"
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          event.target.value = "";
          if (!chosen) return;
          void changeBytes(async () =>
            rpc.booroKnowledge.replaceFile({
              fileId,
              name: chosen.name,
              mimeType: chosen.type,
              contentBase64: await base64Of(chosen),
            }),
          );
        }}
      />
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        {onBack ? (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t`Back to the folder`}
            onClick={onBack}
          >
            <ArrowLeft className="rtl:-scale-x-100" />
          </Button>
        ) : null}
        <Input
          data-testid="knowledge-file-name"
          value={name}
          disabled={busy}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => void rename()}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          aria-label={t`File name`}
          className="min-w-40 max-w-72 flex-1 border-transparent px-1 text-[15px] font-medium shadow-none focus-visible:border-border"
        />
        <span className="flex items-center gap-1 text-[12px] text-muted-foreground">
          {file.spaceId === null ? (
            <Globe size={12} aria-hidden="true" />
          ) : (
            <Building size={12} aria-hidden="true" />
          )}
          <span dir="auto">{placeName}</span>
          {file.folder ? <span dir="auto">· {file.folder}</span> : null}
          <span>· {fileSize(file.size, i18n.locale)}</span>
          {writer ? <span dir="auto">· {t`Kept by ${writer}`}</span> : null}
        </span>
        <div className="ms-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" disabled={!bytes} onClick={download}>
            <Download />
            <Trans>Download</Trans>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => versionInput.current?.click()}
          >
            <Upload />
            <Trans>New version</Trans>
          </Button>
          {versions > 0 ? (
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={t`Back to the previous version`}
              disabled={busy}
              onClick={() =>
                void changeBytes(() => rpc.booroKnowledge.restorePreviousFile({ fileId }))
              }
            >
              <Undo2 />
            </Button>
          ) : null}
          <MoveMenu
            folders={folders
              .filter((folder) => folder.spaceId === file.spaceId)
              .map((folder) => folder.path)}
            current={file.folder}
            onMove={(folder) => void move(folder)}
          />
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t`Delete file`}
            disabled={busy}
            onClick={() => void remove()}
          >
            <Trash2 />
          </Button>
        </div>
      </div>

      <AccessRow
        key={file.id}
        resource={{ kind: "file", id: file.id, spaceId: file.spaceId }}
        snapshot={snapshot}
        onChanged={onChanged}
      />

      <div className="border-b border-border px-4 py-2">
        <Input
          data-testid="knowledge-file-summary"
          value={summary}
          maxLength={400}
          onChange={(event) => setSummary(event.target.value)}
          onBlur={() => void describe()}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          placeholder={t`What is it and what is it for?`}
          aria-label={t`Description`}
          className="border-transparent px-1 text-[13.5px] shadow-none focus-visible:border-border"
        />
      </div>

      {error ? (
        <p role="alert" className="px-4 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex flex-1 items-start justify-center p-4">
        {preview && file.mimeType === "application/pdf" ? (
          <iframe
            title={file.name}
            src={preview}
            className="h-[70vh] w-full rounded-lg border border-border"
          />
        ) : preview ? (
          <img
            src={preview}
            alt={file.name}
            className="max-h-[70vh] max-w-full rounded-lg border border-border bg-muted object-contain"
          />
        ) : text !== null ? (
          <pre className="w-full overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted p-4 font-mono text-[12.5px]">
            {text}
          </pre>
        ) : (
          <p className="pt-12 text-sm text-muted-foreground">
            <Trans>No preview for this file</Trans>
          </p>
        )}
      </div>
    </>
  );
}
